/* ════════════════════════════════════════════════════════════
   PERIOD HELPERS — parse / build / lookups (ES5-safe)
   Extracted from index.jsx — Tier 1 split

   Exports:
     parseYear, parseQ, parseMonth
     rowPeriodKey, periodLabel
     buildPeriodOpts (from raw rows)
     buildPeriodOptsFromDims (from server dimension lists)
     uniq
     buildMatYtdLookup
   ──────────────────────────────────────────────────────────── */

import { MONTH_S, MONTH_N } from "../constants";

export function parseYear(v)  { var n = parseInt(String(v != null ? v : "").trim()); return (n >= 1900 && n <= 2100) ? n : null; }
export function parseQ(v)     { var m = /Q?([1-4])/i.exec(String(v != null ? v : "").trim()); return m ? parseInt(m[1]) : null; }
export function parseMonth(v) {
  var s = String(v != null ? v : "").trim();
  var n = parseInt(s);
  if (!isNaN(n) && n >= 1 && n <= 12) return n;
  var sl = s.toLowerCase().slice(0, 3);
  for (var i = 0; i < MONTH_N.length; i++) { if (MONTH_N[i].toLowerCase().indexOf(sl) === 0) return i + 1; }
  return null;
}
export function rowPeriodKey(row, mp, mode) {
  if (mode === "Yearly")    { var y = parseYear(row[mp.yearCol]); return y ? String(y) : null; }
  if (mode === "Quarterly") { var q = parseQ(row[mp.quarterCol]); var yq = parseYear(row[mp.yearCol]); return (q && yq) ? (yq + "-Q" + q) : null; }
  if (mode === "Monthly")   { var mo = parseMonth(row[mp.monthCol]); var ym = parseYear(row[mp.yearCol]); return (mo && ym) ? (ym + "-" + (mo < 10 ? "0" : "") + mo) : null; }
  if (mode === "TenderStatus") { var yt = parseYear(row[mp.yearCol]); var ts = row[mp.tenderStatus] || row[mp._statusColumn]; return (yt && ts) ? (yt + "_" + ts) : null; }
  return null;
}
/* ── Flow-compound period keys (Sales Tracker sell-in/out header bands) ──
   ANY base period key (2026 / 2026-Q1 / 2026-01 / MAT-Q1-2026 / YTD-2026 / ...)
   can carry a "~Flow" suffix: "2026-Q1~Sell-In". The base key keeps its type,
   sorting, label and growth semantics; the flow suffix travels along. "~" never
   appears in any base key format, so parsing is unambiguous (unlike "_", which
   tender keys use). */
export function splitFlowKey(pk) {
  var s = String(pk || "");
  var i = s.indexOf("~");
  if (i < 0) return { base: s, flow: null };
  return { base: s.slice(0, i), flow: s.slice(i + 1) };
}
/* Real calendar-previous period for any period key. Used by Growth-vs-Previous:
   the comparison base is the TRUE prior period (2026-Q1 → 2025-Q4), never just
   "the previous key in whatever the user happened to select". Returns null for
   unrecognized keys. Formats mirror rowPeriodKey (months zero-padded). */
export function realPrevPeriodKey(pk) {
  /* Flow-compound: previous period of the SAME flow ("2026-Q1~Sell-In" → "2025-Q4~Sell-In") */
  var _fx = String(pk || "").indexOf("~");
  if (_fx >= 0) { var _fb = realPrevPeriodKey(String(pk).slice(0, _fx)); return _fb ? _fb + String(pk).slice(_fx) : null; }
  if (/^\d{4}$/.test(pk)) return String(parseInt(pk) - 1);
  var q = /^(\d{4})-Q([1-4])$/.exec(pk);
  if (q) { var qy = parseInt(q[1]), qq = parseInt(q[2]); return qq > 1 ? qy + "-Q" + (qq - 1) : (qy - 1) + "-Q4"; }
  var m = /^(\d{4})-(\d{1,2})$/.exec(pk);
  if (m) { var my = parseInt(m[1]), mm = parseInt(m[2]); var py = mm > 1 ? my : my - 1; var pm = mm > 1 ? mm - 1 : 12; return py + "-" + (pm < 10 ? "0" : "") + pm; }
  var mat = /^MAT-Q([1-4])-(\d{4})$/.exec(pk);
  if (mat) { var aq = parseInt(mat[1]), ay = parseInt(mat[2]); return aq > 1 ? "MAT-Q" + (aq - 1) + "-" + ay : "MAT-Q4-" + (ay - 1); }
  var matm = /^MATM-(\d{1,2})-(\d{4})$/.exec(pk);
  if (matm) { var bm = parseInt(matm[1]), by = parseInt(matm[2]); return bm > 1 ? "MATM-" + (bm - 1) + "-" + by : "MATM-12-" + (by - 1); }
  var ytd = /^YTD-(\d{4})$/.exec(pk); if (ytd) return "YTD-" + (parseInt(ytd[1]) - 1);
  var ytdm = /^YTDM-(\d{4})$/.exec(pk); if (ytdm) return "YTDM-" + (parseInt(ytdm[1]) - 1);
  var tp = /^(\d{4})_(.+)$/.exec(pk); if (tp) return (parseInt(tp[1]) - 1) + "_" + tp[2];
  return null;
}
/* Same-type "similar period last year" (YoY base) for any period key:
   2026→2025, 2026-Q1→2025-Q1, 2026-03→2025-03, MAT-Q1-2026→MAT-Q1-2025,
   YTD-2026→YTD-2025, 2025_Awarded→2024_Awarded. Returns null if unrecognized. */
export function realYoyPeriodKey(pk) {
  var s = String(pk || "");
  /* Flow-compound: same period last year, SAME flow */
  var _fx = s.indexOf("~");
  if (_fx >= 0) { var _fb = realYoyPeriodKey(s.slice(0, _fx)); return _fb ? _fb + s.slice(_fx) : null; }
  if (/^\d{4}$/.test(s)) return String(parseInt(s) - 1);
  var q = /^(\d{4})-Q([1-4])$/.exec(s); if (q) return (parseInt(q[1]) - 1) + "-Q" + q[2];
  var m = /^(\d{4})-(\d{2})$/.exec(s); if (m) return (parseInt(m[1]) - 1) + "-" + m[2];
  var mat = /^MAT-Q([1-4])-(\d{4})$/.exec(s); if (mat) return "MAT-Q" + mat[1] + "-" + (parseInt(mat[2]) - 1);
  var matm = /^MATM-(\d{1,2})-(\d{4})$/.exec(s); if (matm) return "MATM-" + matm[1] + "-" + (parseInt(matm[2]) - 1);
  var ytd = /^YTD-(\d{4})$/.exec(s); if (ytd) return "YTD-" + (parseInt(ytd[1]) - 1);
  var ytdm = /^YTDM-(\d{4})$/.exec(s); if (ytdm) return "YTDM-" + (parseInt(ytdm[1]) - 1);
  var tp = /^(\d{4})_(.+)$/.exec(s); if (tp) return (parseInt(tp[1]) - 1) + "_" + tp[2];
  return null;
}
/* ── Period TYPE detection — every period key is self-typed by its format ──
   Used by the multi-period-type feature: a single query may mix Monthly,
   Quarterly, Yearly, MAT and YTD keys; each key's type determines which
   band it renders in and which previous/similar period its growth uses. */
export function periodKeyType(pk) {
  var s = String(pk || "");
  /* Flow-compound keys keep the BASE key's type: "2026-Q1~Sell-In" is Quarterly.
     Banding, growth and sort ordering all follow the base type. */
  var _fx = s.indexOf("~");
  if (_fx >= 0) s = s.slice(0, _fx);
  if (/^\d{4}$/.test(s)) return "Yearly";
  if (/^\d{4}-Q[1-4]$/.test(s)) return "Quarterly";
  if (/^\d{4}-\d{2}$/.test(s)) return "Monthly";
  if (/^MAT-Q[1-4]-\d{4}$/.test(s)) return "MAT";
  if (/^MATM-\d{1,2}-\d{4}$/.test(s)) return "MAT Monthly";
  if (/^YTD-\d{4}$/.test(s)) return "YTD";
  if (/^YTDM-\d{4}$/.test(s)) return "YTD Monthly";
  if (/^\d{4}_.+$/.test(s)) return "TenderStatus";
  return null;
}
/* Band metadata: display order (granular → aggregate) + user-facing band label. */
export var PERIOD_TYPE_META = {
  "Monthly":     { order: 1, label: "Monthly" },
  "Quarterly":   { order: 2, label: "Quarterly" },
  "Yearly":      { order: 3, label: "Full Year" },
  "MAT":         { order: 4, label: "MAT (Quarterly)" },
  "MAT Monthly": { order: 5, label: "MAT (Monthly)" },
  "YTD":         { order: 6, label: "YTD (Quarterly)" },
  "YTD Monthly": { order: 7, label: "YTD (Monthly)" },
  "TenderStatus":{ order: 8, label: "Tender" }
};
export function periodLabel(key) {
  if (!key || key === "ALL") return "All Periods";
  /* Flow-compound: base label + flow ("2026-Q1~Sell-In" → "Q1 2026 Sell-In") */
  var _fx = String(key).indexOf("~");
  if (_fx >= 0) return periodLabel(String(key).slice(0, _fx)) + " " + String(key).slice(_fx + 1);
  if (key === "__PTOTAL__") return "\u03A3 Total \u2014 Selected Periods";
  if (key === "__PTOTALY__") return "\u03A3 Total (previous year)";
  if (/^\d{4}$/.test(key)) return key;
  var q = /^(\d{4})-Q([1-4])$/.exec(key); if (q) return "Q" + q[2] + " " + q[1];
  var m = /^(\d{4})-(\d{2})$/.exec(key);  if (m) return MONTH_S[parseInt(m[2]) - 1] + " " + m[1];
  var mat = /^MAT-Q([1-4])-(\d{4})$/.exec(key); if (mat) return "MAT Q" + mat[1] + " " + mat[2];
  var matm = /^MATM-(\d{1,2})-(\d{4})$/.exec(key); if (matm) { var mm=parseInt(matm[1]); return "MAT " + MONTH_S[mm-1] + " " + matm[2]; }
  var ytd = /^YTD-(\d{4})$/.exec(key); if (ytd) return "YTD " + ytd[1];
  var ytdm = /^YTDM-(\d{4})$/.exec(key); if (ytdm) return "YTD " + ytdm[1] + " (M)";
  /* Tender status: "2024_Awarded" → "2024 Awarded" */
  var tp = /^(\d{4})_(.+)$/.exec(key); if (tp) return tp[1] + " " + tp[2];
  return key;
}
export function buildPeriodOpts(rows, mp) {
  /* Use plain objects as ordered maps: key → entry */
  var Yobj = {}, Ykeys = [], Qobj = {}, Qkeys = [], Mobj = {}, Mkeys = [];
  for (var ri = 0; ri < rows.length; ri++) {
    var row = rows[ri];
    var y = parseYear(row[mp.yearCol]);
    if (y) {
      var yk = String(y);
      if (!Yobj[yk]) { Yobj[yk] = {key:yk, label:yk, year:y}; Ykeys.push(yk); }
    }
    if (mp.quarterCol && y) {
      var q = parseQ(row[mp.quarterCol]);
      if (q) { var qk = y + "-Q" + q; if (!Qobj[qk]) { Qobj[qk] = {key:qk, label:"Q"+q+" "+y, year:y, q:q}; Qkeys.push(qk); } }
    }
    if (mp.monthCol && y) {
      var mo = parseMonth(row[mp.monthCol]);
      if (mo) { var mk = y + "-" + (mo < 10 ? "0" : "") + mo; if (!Mobj[mk]) { Mobj[mk] = {key:mk, label:MONTH_S[mo-1]+" "+y, year:y, m:mo}; Mkeys.push(mk); } }
    }
  }
  var Ya = Ykeys.map(function(k){ return Yobj[k]; }).sort(function(a,b){ return a.year - b.year; });
  var Qa = Qkeys.map(function(k){ return Qobj[k]; }).sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.q - b.q; });
  var Ma = Mkeys.map(function(k){ return Mobj[k]; }).sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.m - b.m; });

  /* ── MAT (Moving Annual Total) — last 4 quarters ending at each quarter ── */
  var MATa = [];
  var hasQ = Qa.length > 0;
  var hasM = Ma.length > 0;
  if (hasQ) {
    /* Quarter-based MAT: need 4 consecutive quarters */
    var availQ = {};
    for (var qi2 = 0; qi2 < Qa.length; qi2++) { availQ[Qa[qi2].year + "-" + Qa[qi2].q] = true; }
    for (var qi3 = 0; qi3 < Qa.length; qi3++) {
      var endY = Qa[qi3].year, endQ = Qa[qi3].q;
      /* Walk back 4 quarters from this one */
      var allOk = true;
      var matQList = [];
      var cY = endY, cQ = endQ;
      for (var qb = 0; qb < 4; qb++) {
        if (!availQ[cY + "-" + cQ]) { allOk = false; break; }
        matQList.push({year:cY, q:cQ});
        cQ--; if (cQ === 0) { cQ = 4; cY--; }
      }
      if (allOk) {
        var matKey = "MAT-Q" + endQ + "-" + endY;
        MATa.push({key:matKey, label:"MAT Q"+endQ+" "+endY, year:endY, q:endQ, quarters:matQList});
      }
    }
    MATa.sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.q - b.q; });
  } else if (hasM) {
    /* Month-based MAT fallback: need 12 consecutive months */
    var availMo = {};
    for (var mi2 = 0; mi2 < Ma.length; mi2++) { availMo[Ma[mi2].year + "-" + Ma[mi2].m] = true; }
    var qEndMonths = [3, 6, 9, 12];
    var allYears = Ya.map(function(y){ return y.year; });
    var minY = allYears.length ? allYears[0] : 0;
    var maxY = allYears.length ? allYears[allYears.length-1] : 0;
    for (var my = minY; my <= maxY; my++) {
      for (var qi4 = 0; qi4 < 4; qi4++) {
        var endM = qEndMonths[qi4], qNum = qi4 + 1;
        var matMonths = [];
        for (var off = 11; off >= 0; off--) {
          var cm = endM - off, cy = my;
          while (cm <= 0) { cm += 12; cy--; }
          matMonths.push({year:cy, month:cm});
        }
        var allAvail = true;
        for (var mc = 0; mc < matMonths.length; mc++) {
          if (!availMo[matMonths[mc].year + "-" + matMonths[mc].month]) { allAvail = false; break; }
        }
        if (allAvail) {
          var matKey2 = "MAT-Q" + qNum + "-" + my;
          MATa.push({key:matKey2, label:"MAT Q"+qNum+" "+my, year:my, q:qNum, months:matMonths});
        }
      }
    }
    MATa.sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.q - b.q; });
  }

  /* ── YTD Quarterly — cap all years at latest year's last quarter ── */
  var YTDa = [];
  if (hasQ) {
    var yearQMap = {};
    for (var qi5 = 0; qi5 < Qa.length; qi5++) {
      var qy = Qa[qi5].year;
      if (!yearQMap[qy]) yearQMap[qy] = [];
      yearQMap[qy].push(Qa[qi5].q);
    }
    var ytdYearsQ = Object.keys(yearQMap).map(Number).sort(function(a,b){return a-b;});
    var latestYrQ = ytdYearsQ[ytdYearsQ.length - 1];
    var latestQsA = (yearQMap[latestYrQ]||[]).sort(function(a,b){return a-b;});
    var capQ = 0;
    for (var cqi = 0; cqi < latestQsA.length; cqi++) { if (latestQsA[cqi] === cqi + 1) capQ = cqi + 1; else break; }
    if (capQ === 0) capQ = 1;
    for (var yi = 0; yi < ytdYearsQ.length; yi++) {
      var ytdY = ytdYearsQ[yi];
      var qs = (yearQMap[ytdY]||[]).sort(function(a,b){return a-b;});
      var ytdQs = [];
      for (var yq = 0; yq < Math.min(qs.length, capQ); yq++) {
        if (qs[yq] === yq + 1) ytdQs.push({year:ytdY, q:qs[yq]});
        else break;
      }
      if (ytdQs.length > 0) {
        var lastQ = ytdQs[ytdQs.length-1].q;
        var ytdKey = "YTD-" + ytdY;
        YTDa.push({key:ytdKey, label:"YTD " + ytdY + " (Q1" + (lastQ>1?"-Q"+lastQ:"") + ")", year:ytdY, quarters:ytdQs, lastQ:lastQ});
      }
    }
  }

  /* ── YTD Monthly — cap all years at latest year's last month ── */
  var YTDMa = [];
  if (hasM) {
    var yearMMap = {};
    for (var mi3 = 0; mi3 < Ma.length; mi3++) {
      var yy = Ma[mi3].year;
      if (!yearMMap[yy]) yearMMap[yy] = [];
      yearMMap[yy].push(Ma[mi3].m);
    }
    var ytdYearsM = Object.keys(yearMMap).map(Number).sort(function(a,b){return a-b;});
    var latestYrM = ytdYearsM[ytdYearsM.length - 1];
    var latestMsA = (yearMMap[latestYrM]||[]).sort(function(a,b){return a-b;});
    var capM = 0;
    for (var cmi = 0; cmi < latestMsA.length; cmi++) { if (latestMsA[cmi] === cmi + 1) capM = cmi + 1; else break; }
    if (capM === 0) capM = 1;
    for (var yi2 = 0; yi2 < ytdYearsM.length; yi2++) {
      var ytdY2 = ytdYearsM[yi2];
      var mths = (yearMMap[ytdY2]||[]).sort(function(a,b){return a-b;});
      var ytdMonths = [];
      for (var ym = 0; ym < Math.min(mths.length, capM); ym++) {
        if (mths[ym] === ym + 1) ytdMonths.push({year:ytdY2, month:mths[ym]});
        else break;
      }
      if (ytdMonths.length > 0) {
        var lastM = ytdMonths[ytdMonths.length-1].month;
        var ytdKey2 = "YTDM-" + ytdY2;
        YTDMa.push({key:ytdKey2, label:"YTD " + ytdY2 + " (Jan" + (lastM>1?"-"+MONTH_S[lastM-1]:"") + ")", year:ytdY2, months:ytdMonths, lastMonth:lastM});
      }
    }
  }

  /* ── MAT Monthly — 12 consecutive months ending at each month ── */
  var MATMa = [];
  if (Ma.length >= 12) {
    var availMoMap = {};
    for (var mmi = 0; mmi < Ma.length; mmi++) availMoMap[Ma[mmi].year + "-" + Ma[mmi].m] = true;
    for (var mmj = 0; mmj < Ma.length; mmj++) {
      var eYm = Ma[mmj].year, eMm = Ma[mmj].m;
      var all12 = true, wYm = eYm, wMm = eMm;
      for (var mck = 0; mck < 12; mck++) {
        if (!availMoMap[wYm + "-" + wMm]) { all12 = false; break; }
        wMm--; if (wMm === 0) { wMm = 12; wYm--; }
      }
      if (all12) {
        var sM = eMm + 1, sY = eYm - 1; if (sM > 12) { sM -= 12; sY++; }
        var matMKey = "MATM-" + eMm + "-" + eYm;
        var matMMonths1 = [];
        var wYb1 = eYm, wMb1 = eMm;
        for (var mc1 = 0; mc1 < 12; mc1++) { matMMonths1.push({year:wYb1, month:wMb1}); wMb1--; if (wMb1 === 0) { wMb1 = 12; wYb1--; } }
        MATMa.push({key:matMKey, label:"MAT " + MONTH_S[eMm-1] + " " + eYm + " (" + MONTH_S[sM-1] + " " + sY + " → " + MONTH_S[eMm-1] + " " + eYm + ")", year:eYm, m:eMm, startYear:sY, startMonth:sM, months:matMMonths1});
      }
    }
    MATMa.sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.m - b.m; });
  }

  return { years:Ya, quarters:Qa, months:Ma, mats:MATa, ytds:YTDa, ytdMonthly:YTDMa, matMonths:MATMa };
}

/* Build period options from server-supplied dimension values (no rows needed) */
export function buildPeriodOptsFromDims(dimValues) {
  if (!dimValues) return {years:[],quarters:[],months:[],mats:[],ytds:[],matMonths:[]};
  var MONTH_SH = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  /* Years */
  var Ya = (dimValues.years || []).map(function(y){ y=Number(y); return {key:String(y), label:String(y), year:y}; })
    .sort(function(a,b){ return a.year - b.year; });

  /* Quarters */
  var Qa = (dimValues.quarters || []).map(function(qr){ var y=qr.year, q=qr.quarter; return {key:y+"-Q"+q, label:"Q"+q+" "+y, year:y, q:q}; })
    .sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.q - b.q; });

  /* Months */
  var Ma = (dimValues.months || []).map(function(mr){ var y=mr.year, m=mr.month; return {key:y+"-"+(m<10?"0":"")+m, label:MONTH_SH[m-1]+" "+y, year:y, m:m}; })
    .sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.m - b.m; });

  /* MAT from quarters */
  var MATa = [];
  if (Qa.length > 0) {
    var availQ = {};
    for (var qi2 = 0; qi2 < Qa.length; qi2++) { availQ[Qa[qi2].year + "-" + Qa[qi2].q] = true; }
    for (var qi3 = 0; qi3 < Qa.length; qi3++) {
      var endY = Qa[qi3].year, endQ = Qa[qi3].q;
      var allOk = true, matQList = [], cY = endY, cQ = endQ;
      for (var qb = 0; qb < 4; qb++) {
        if (!availQ[cY + "-" + cQ]) { allOk = false; break; }
        matQList.push({year:cY, q:cQ});
        cQ--; if (cQ === 0) { cQ = 4; cY--; }
      }
      if (allOk) MATa.push({key:"MAT-Q"+endQ+"-"+endY, label:"MAT Q"+endQ+" "+endY, year:endY, q:endQ, quarters:matQList});
    }
    MATa.sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.q - b.q; });
  } else if (Ma.length > 0) {
    var availMo = {};
    for (var mi2 = 0; mi2 < Ma.length; mi2++) { availMo[Ma[mi2].year + "-" + Ma[mi2].m] = true; }
    var qEndMonths = [3, 6, 9, 12];
    var minY = Ya.length ? Ya[0].year : 0, maxY = Ya.length ? Ya[Ya.length-1].year : 0;
    for (var my = minY; my <= maxY; my++) {
      for (var qi4 = 0; qi4 < 4; qi4++) {
        var endM = qEndMonths[qi4], qNum = qi4 + 1;
        var matMonths = [];
        for (var off = 11; off >= 0; off--) {
          var cm = endM - off, cy = my;
          while (cm <= 0) { cm += 12; cy--; }
          matMonths.push({year:cy, month:cm});
        }
        var allAvail = true;
        for (var mc = 0; mc < matMonths.length; mc++) { if (!availMo[matMonths[mc].year + "-" + matMonths[mc].month]) { allAvail = false; break; } }
        if (allAvail) MATa.push({key:"MAT-Q"+qNum+"-"+my, label:"MAT Q"+qNum+" "+my, year:my, q:qNum, months:matMonths});
      }
    }
    MATa.sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.q - b.q; });
  }

  /* YTD Quarterly — cap all years at the latest year's last available quarter */
  var YTDa = [];
  if (Qa.length > 0) {
    var yearQMap = {};
    for (var qi5 = 0; qi5 < Qa.length; qi5++) { var qy = Qa[qi5].year; if (!yearQMap[qy]) yearQMap[qy] = []; yearQMap[qy].push(Qa[qi5].q); }
    var ytdYearsQ = Object.keys(yearQMap).map(Number).sort(function(a,b){return a-b;});
    /* Find the latest year's contiguous quarter count (the cap) */
    var latestYearQ = ytdYearsQ[ytdYearsQ.length - 1];
    var latestQs = (yearQMap[latestYearQ]||[]).sort(function(a,b){return a-b;});
    var capQ = 0;
    for (var cqi = 0; cqi < latestQs.length; cqi++) { if (latestQs[cqi] === cqi + 1) capQ = cqi + 1; else break; }
    if (capQ === 0) capQ = 1;
    /* Build YTD for each year, capped at capQ */
    for (var yi = 0; yi < ytdYearsQ.length; yi++) {
      var ytdY = ytdYearsQ[yi];
      var qs = (yearQMap[ytdY]||[]).sort(function(a,b){return a-b;});
      var ytdQs = [];
      for (var yq = 0; yq < Math.min(qs.length, capQ); yq++) { if (qs[yq] === yq + 1) ytdQs.push({year:ytdY, q:qs[yq]}); else break; }
      if (ytdQs.length > 0) {
        var lastQ = ytdQs[ytdQs.length-1].q;
        YTDa.push({key:"YTD-"+ytdY, label:"YTD "+ytdY+" (Q1"+(lastQ>1?"-Q"+lastQ:"")+")", year:ytdY, quarters:ytdQs, lastQ:lastQ});
      }
    }
  }

  /* YTD Monthly — cap all years at the latest year's last available month */
  var YTDMa = [];
  if (Ma.length > 0) {
    var yearMMap = {};
    for (var mi3 = 0; mi3 < Ma.length; mi3++) { var yy = Ma[mi3].year; if (!yearMMap[yy]) yearMMap[yy] = []; yearMMap[yy].push(Ma[mi3].m); }
    var ytdYearsM = Object.keys(yearMMap).map(Number).sort(function(a,b){return a-b;});
    /* Find the latest year's contiguous month count (the cap) */
    var latestYearM = ytdYearsM[ytdYearsM.length - 1];
    var latestMs = (yearMMap[latestYearM]||[]).sort(function(a,b){return a-b;});
    var capM = 0;
    for (var cmi = 0; cmi < latestMs.length; cmi++) { if (latestMs[cmi] === cmi + 1) capM = cmi + 1; else break; }
    if (capM === 0) capM = 1;
    /* Build YTD for each year, capped at capM */
    for (var yi2 = 0; yi2 < ytdYearsM.length; yi2++) {
      var ytdY2 = ytdYearsM[yi2];
      var mths = (yearMMap[ytdY2]||[]).sort(function(a,b){return a-b;});
      var ytdMonths = [];
      for (var ym = 0; ym < Math.min(mths.length, capM); ym++) { if (mths[ym] === ym + 1) ytdMonths.push({year:ytdY2, month:mths[ym]}); else break; }
      if (ytdMonths.length > 0) {
        var lastM = ytdMonths[ytdMonths.length-1].month;
        YTDMa.push({key:"YTDM-"+ytdY2, label:"YTD "+ytdY2+" (Jan"+(lastM>1?"-"+MONTH_SH[lastM-1]:"")+")", year:ytdY2, months:ytdMonths, lastMonth:lastM});
      }
    }
  }

  /* ── MAT Monthly from dims ── */
  var MATMa2 = [];
  if (Ma.length >= 12) {
    var availMoMap2 = {};
    for (var mmi2 = 0; mmi2 < Ma.length; mmi2++) availMoMap2[Ma[mmi2].year + "-" + Ma[mmi2].m] = true;
    for (var mmj2 = 0; mmj2 < Ma.length; mmj2++) {
      var eYm2 = Ma[mmj2].year, eMm2 = Ma[mmj2].m;
      var all12b = true, wYm2 = eYm2, wMm2 = eMm2;
      for (var mck2 = 0; mck2 < 12; mck2++) {
        if (!availMoMap2[wYm2 + "-" + wMm2]) { all12b = false; break; }
        wMm2--; if (wMm2 === 0) { wMm2 = 12; wYm2--; }
      }
      if (all12b) {
        var sM2 = eMm2 + 1, sY2b = eYm2 - 1; if (sM2 > 12) { sM2 -= 12; sY2b++; }
        var matMMonths = [];
        var wYb = eYm2, wMb = eMm2;
        for (var mc2 = 0; mc2 < 12; mc2++) { matMMonths.push({year:wYb, month:wMb}); wMb--; if (wMb === 0) { wMb = 12; wYb--; } }
        MATMa2.push({key:"MATM-"+eMm2+"-"+eYm2, label:"MAT "+MONTH_SH[eMm2-1]+" "+eYm2+" ("+MONTH_SH[sM2-1]+" "+sY2b+" → "+MONTH_SH[eMm2-1]+" "+eYm2+")", year:eYm2, m:eMm2, months:matMMonths});
      }
    }
    MATMa2.sort(function(a,b){ return a.year !== b.year ? a.year - b.year : a.m - b.m; });
  }

  return { years:Ya, quarters:Qa, months:Ma, mats:MATa, ytds:YTDa, ytdMonthly:YTDMa, matMonths:MATMa2 };
}
export function uniq(rows, col) {
  if (!col) return [];
  var seen = {}, out = [];
  for (var i = 0; i < rows.length; i++) {
    var v = String(rows[i][col] != null ? rows[i][col] : "");
    if (v && v !== "undefined" && !seen[v]) { seen[v] = 1; out.push(v); }
  }
  return out.sort();
}
/* ── MAT/YTD period-membership helper ── */
export function buildMatYtdLookup(periodKeys, pOpts) {
  /* Returns a map: "YYYY-Q#" or "YYYY-MM" → [periodKey, ...] */
  var lookup = {};
  if (!pOpts) return lookup;
  for (var i = 0; i < periodKeys.length; i++) {
    var pk = periodKeys[i];
    var matMatch = /^MAT-Q([1-4])-(\d{4})$/.exec(pk);
    var matMMatch = /^MATM-(\d{1,2})-(\d{4})$/.exec(pk);
    var ytdMatch = /^YTD-(\d{4})$/.exec(pk);
    var ytdMMatch = /^YTDM-(\d{4})$/.exec(pk);
    var src = null;
    if (matMatch) {
      for (var j = 0; j < (pOpts.mats||[]).length; j++) { if (pOpts.mats[j].key === pk) { src = pOpts.mats[j]; break; } }
    } else if (matMMatch) {
      for (var jm = 0; jm < (pOpts.matMonths||[]).length; jm++) { if (pOpts.matMonths[jm].key === pk) { src = pOpts.matMonths[jm]; break; } }
    } else if (ytdMatch) {
      for (var j2 = 0; j2 < (pOpts.ytds||[]).length; j2++) { if (pOpts.ytds[j2].key === pk) { src = pOpts.ytds[j2]; break; } }
    } else if (ytdMMatch) {
      for (var j3 = 0; j3 < (pOpts.ytdMonthly||[]).length; j3++) { if (pOpts.ytdMonthly[j3].key === pk) { src = pOpts.ytdMonthly[j3]; break; } }
    }
    if (!src) {
      /* Hidden growth-base keys (real-previous / same-window-last-year) are often
         OUTSIDE the UI's selectable period options, so they have no pOpts entry.
         Their windows are still fully deterministic — synthesize them here so the
         growth bases accumulate (MATM-6-2025 = the 12 months ending Jun 2025). */
      if (matMatch) {
        var _sq = parseInt(matMatch[1]), _sy = parseInt(matMatch[2]);
        src = { key: pk, quarters: [] };
        for (var _k9 = 0; _k9 < 4; _k9++) {
          var _qq = _sq - _k9, _qy = _sy;
          while (_qq < 1) { _qq += 4; _qy -= 1; }
          src.quarters.push({ year: _qy, q: _qq });
        }
      } else if (matMMatch) {
        var _sm = parseInt(matMMatch[1]), _smy = parseInt(matMMatch[2]);
        src = { key: pk, months: [] };
        for (var _m9 = 0; _m9 < 12; _m9++) {
          var _m2 = _sm - _m9, _y2 = _smy;
          while (_m2 < 1) { _m2 += 12; _y2 -= 1; }
          src.months.push({ year: _y2, month: _m2 });
        }
      } else if (ytdMatch || ytdMMatch) {
        /* YTD windows depend on the data cutoff — mirror NEXT year's (selected)
           window shifted one year back, so YTD-2025 has the same cutoff as YTD-2026. */
        var _yy9 = parseInt((ytdMatch || ytdMMatch)[1]);
        var _nk = ytdMatch ? ("YTD-" + (_yy9 + 1)) : ("YTDM-" + (_yy9 + 1));
        var _lists = ytdMatch ? (pOpts.ytds || []) : (pOpts.ytdMonthly || []);
        var _nsrc = null;
        for (var _nn = 0; _nn < _lists.length; _nn++) { if (_lists[_nn].key === _nk) { _nsrc = _lists[_nn]; break; } }
        if (_nsrc) {
          src = { key: pk };
          if (_nsrc.quarters) { src.quarters = _nsrc.quarters.map(function(qq){ return { year: qq.year - 1, q: qq.q }; }); }
          if (_nsrc.months) { src.months = _nsrc.months.map(function(mm){ return { year: mm.year - 1, month: mm.month }; }); }
        }
      }
      if (!src) continue;
    }
    if (src.quarters) {
      for (var k = 0; k < src.quarters.length; k++) {
        var qk = src.quarters[k].year + "-Q" + src.quarters[k].q;
        if (!lookup[qk]) lookup[qk] = [];
        lookup[qk].push(pk);
      }
    }
    if (src.months) {
      for (var k2 = 0; k2 < src.months.length; k2++) {
        var mk = src.months[k2].year + "-" + src.months[k2].month;
        if (!lookup[mk]) lookup[mk] = [];
        lookup[mk].push(pk);
      }
    }
  }
  return lookup;
}
